import type { TicketStatus, TicketPriority } from "./api";
import { STATUS_HUE } from "./statusColors";

export const STATUS_COLUMNS: { status: TicketStatus; label: string }[] = [
  { status: "backlog", label: "Backlog" },
  { status: "todo", label: "To do" },
  { status: "in_progress", label: "In progress" },
  { status: "review", label: "Review" },
  { status: "done", label: "Done" },
];

/** Jira-style priority icons (colored chevrons) + ordering rank. */
export const PRIORITY_META: Record<
  TicketPriority,
  { label: string; icon: string; color: string; rank: number }
> = {
  urgent: { label: "Urgent", icon: "fa-angles-up", color: STATUS_HUE.red, rank: 3 },
  high: { label: "High", icon: "fa-angle-up", color: STATUS_HUE.orange, rank: 2 },
  medium: { label: "Medium", icon: "fa-equals", color: STATUS_HUE.amber, rank: 1 },
  low: { label: "Low", icon: "fa-angle-down", color: STATUS_HUE.blue, rank: 0 },
};

export const PRIORITY_ORDER: TicketPriority[] = ["urgent", "high", "medium", "low"];

/** Small accent color per status, used for the column header dot/line. */
export const STATUS_COLOR: Record<TicketStatus, string> = {
  backlog: STATUS_HUE.neutral,
  todo: STATUS_HUE.blue,
  in_progress: STATUS_HUE.amber,
  review: STATUS_HUE.purple,
  done: STATUS_HUE.green,
};

/**
 * The board's ordering within a column: priority first, then oldest first so
 * a tie is stable rather than arbitrary.
 */
export const byPriority = <T extends { priority: TicketPriority; createdAt: string }>(
  a: T,
  b: T,
): number =>
  PRIORITY_META[b.priority].rank - PRIORITY_META[a.priority].rank ||
  a.createdAt.localeCompare(b.createdAt);

/**
 * The board's reading order: down each column in turn, in column order.
 *
 * This is what the ticket modal's arrows walk, so "next" always means the
 * card the eye would reach next. It takes the already-filtered list, so
 * navigation never jumps to a ticket the current filter is hiding.
 */
export function boardOrder<T extends { status: TicketStatus; priority: TicketPriority; createdAt: string }>(
  tickets: T[],
): T[] {
  return STATUS_COLUMNS.flatMap((col) =>
    tickets.filter((t) => t.status === col.status).sort(byPriority),
  );
}
